import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { FadeIn } from './FadeIn';
type Slide = {
  src: string;
  alt: string;
  label: string;
  title: string;
  desc: string;
};
const slides: Slide[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/ta75cdvCsd6rKkaBUVqHXC/Bono-4-Atlas-Visual-de-Aberturas-de-Aluminio-19.webp',
  alt: 'Plano 15 — Detalle de posición de falleba y cerradura para ventanas y puertas',
  label: 'BONO 4 · Atlas Visual',
  title: 'Detalle: Posición de Falleba y Cerradura',
  desc: 'Medidas exactas (150mm, 550mm, 60mm, 200mm) para fallebas F1 y F5. Cero errores de calado.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gvWFzzSg8o41NRHqy9nXe9/Bono-4-Atlas-Visual-de-Aberturas-de-Aluminio-21.webp',
  alt: 'Plano 17 — Sistema de sellado en 4 capas con orden de aplicación',
  label: 'TOMO 5 · Instalación',
  title: 'Sistema de Sellado en 4 Capas',
  desc: 'Orden exacto: cinta butilénica, espuma PU, silicona exterior, silicona interior. Sin filtraciones.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iuL6qEeKFYWaaym9cHBGGL/Bono-4-Atlas-Visual-de-Aberturas-de-Aluminio-24.webp',
  alt: 'Comparativa visual de tipologías corredizas vs de abrir',
  label: 'TOMO 3 · Guía de Armado',
  title: 'Comparativa: Corredizas vs De Abrir',
  desc: 'Cuándo usar cada tipología, qué herrajes lleva, y el detalle que más se olvida en cada una.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xoKpcKXEquP9G7vWd19pHV/Tomo-2-El-Mapa-de-Descuentos-Lineas-de-Alta-Gama-1-_1_.webp',
  alt: 'Capítulo 6 — Los 5 errores más frecuentes en cortes a 45 grados y cómo evitarlos',
  label: 'TOMO 2 · Módena',
  title: 'Los 5 Errores en Cortes a 45° y Cómo Evitarlos',
  desc: 'Sierra mal calibrada, perfil mal posicionado, rebaba, escuadra y dilatación térmica. Resueltos.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/w6zmYavhKw1VicFuxQrxoY/Tomo-2-El-Mapa-de-Descuentos-Lineas-de-Alta-Gama-2.webp',
  alt: 'Capítulo 8 — Protocolo de instalación en obra: cero problemas post-entrega',
  label: 'TOMO 5 · Instalación',
  title: 'Protocolo de Instalación: Cero Problemas Post-Entrega',
  desc: 'Verificación del vano, fijación del marco, sellado perimetral y entrega documentada. Sin reclamos.'
}];
export function ContentPreview() {
  const [current, setCurrent] = useState(0);
  // Gate the heavy slide image behind viewport visibility so it doesn't
  // compete with the hero LCP for bandwidth on initial load.
  const [imagesReady, setImagesReady] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const total = slides.length;
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  const goTo = useCallback((i: number) => {
    setCurrent((i % total + total) % total);
  }, [total]);
  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    }
  };
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (Math.abs(touchDeltaX.current) > 50) {
      if (touchDeltaX.current < 0) next();else prev();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };
  // Wait until the carousel is near the viewport before downloading the
  // (heavy) slide images. Keeps initial paint snappy.
  useEffect(() => {
    if (imagesReady) return;
    const el = sectionRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setImagesReady(true);
      return;
    }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setImagesReady(true);
        io.disconnect();
      }
    }, {
      rootMargin: '300px'
    });
    io.observe(el);
    return () => io.disconnect();
  }, [imagesReady]);
  // Only load the active slide once images are ready. Adjacent slides
  // load lazily after user interaction (swipe / next / prev).
  const shouldLoad = (i: number) => {
    if (!imagesReady) return false;
    if (i === current) return true;
    // Once user moves at least once, also keep neighbors warm for instant nav
    if (current !== 0) {
      if (i === (current + 1) % total) return true;
      if (i === (current - 1 + total) % total) return true;
    }
    return false;
  };
  const slide = slides[current];
  return;
}